import { useBoolean, useOutsideClick } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { useCacheFilters } from 'hooks/useCacheFilters'
import { ReportManagementQueryDTO } from 'models/ReportQueryDTO'
import { ManagementReportInterface } from 'pages/Report/interface'
import { TableReportKeys } from 'pages/Report/types'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getManagementReports } from 'services/report'
import formatter from 'utils/formatter'
import { initialFiltersManagementReport } from '..'
import { Filter } from '../../Filter/Filter'
import { FilterManagementReport } from '../../Filter/interfaces'
import { Totalizers } from '../../Totalizers'
import { TotalizersItems } from '../../Totalizers/type'
import { BadgeStatus } from '../BadgeStatus'
import { defaultHeaders, defaultLoaders } from './constants'

interface Props {
    onFilter: (filter: FilterManagementReport) => void
}

interface RequestReportsDTO {
    query?: any
    page?: number
    limit?: number
}

export const ModeTable: React.FC<Props> = ({ onFilter }) => {
    const [loading, setLoading] = useState(false)
    const [filter, setFilter] = useState<FilterManagementReport>(
        initialFiltersManagementReport
    )

    const [listTotalizers, setListTotalizers] = useState<TotalizersItems>()
    const navigate = useNavigate()

    const { isBackoffice } = useAuthContext()
    const { currentAgent, currentInstitution, institutions, agents } =
        useInstitutionsContext()

    const [table, setTable] = useState<TableBuildProps<TableReportKeys>>({
        header: defaultHeaders as any,
        action: [
            {
                type: 'PREVIEW'
            }
        ]
    })
    const [pageReports, setPageReports] = useState<number>(1)

    const [limitReports, setLimitReports] = useState<number>(10)
    const [totalPages, setTotalPages] = useState(1)
    const refTooltip = useRef(null)

    const [tooltipState, setTooltipState] = useBoolean(false)

    useOutsideClick({
        ref: refTooltip,
        handler: setTooltipState.off
    })

    const { persistCacheFilters, getCacheFilters, hasCacheFilters } =
        useCacheFilters()

    const setCacheFilters = (id: string) => {
        const cacheData = {
            currentInstitution,
            institutions,
            page: pageReports,
            limit: limitReports,
            from: 'relatorio',
            to: 'contratos/' + id,
            filter
        }

        persistCacheFilters(cacheData)
    }

    const handlePage = (page: number) => {
        if (page !== pageReports) {
            setPageReports(page)
            setLoading(true)

            fetchListReports({
                query: { ...(filter ?? {}) },
                page,
                limit: limitReports
            })
        }
    }

    const handleLimit = (limit: number) => {
        setPageReports(1)
        setLimitReports(limit)
        setLoading(true)

        fetchListReports({
            query: { ...(filter ?? {}) },
            limit,
            page: 1
        })
    }

    const handleAction = (
        _: TableBuildActionsType,
        indexRow: number | string
    ) => {
        const item: any = table.body?.find((_, index) => index === indexRow)

        const guidContrato = item?.guidContrato
        const guidOperation = item?.guidOperacao

        if (guidContrato || guidOperation) {

            const isRefused = item.textStatus === 'RECUSADO';

            const id = isRefused ? guidOperation : guidContrato;
            setCacheFilters(id)
            navigate(
                `/contratos/${id}?typeContract=${item.textStatus}&from=relatorio`
            )
            return
        }
    }

    const buildTable = (managementReport: ManagementReportInterface) => {
        return managementReport.operacoes.map(operation => {
            return {
                ...operation,
                tipoOperacao: operation.tipoOperacao ??'',
                textStatus: operation.status,
                status: <BadgeStatus status={operation?.status} />,
                dataContrato: formatter.data(operation.dataContrato),
                dataRegistro: formatter.data(operation.dataRegistro),
                valorContrato: formatter.moeda(operation.valorContrato),
                possuiImagem: operation.possuiImagem ? 'Sim' : 'Não',
                origem: (
                    <span style={{ textAlign: 'center', display: 'block' }}>
                        {operation.origem}
                    </span>
                )
            }
        })
    }

    const buildQuery = (query: any): ReportManagementQueryDTO => {
        const cnpjAgente = currentAgent?.cnpj ?? ''
        return query ? { ...query, cnpjAgente } : { cnpjAgente }
    }

    const formatPayloadFilter = (
        value: FilterManagementReport
    ): FilterManagementReport => {
        if (!value) return {} as FilterManagementReport

        let payload = {} as FilterManagementReport

        Object.keys(value).forEach(key => {
            payload = {
                ...payload,
                [key]: value[key as keyof FilterManagementReport] ?? ''
            }
        })

        if (value?.periodoFinal || value?.periodoInicial) {
            payload = {
                ...payload,
                periodoFinal: value.periodoFinal
                    ? formatter.formatDateToPayload(value.periodoFinal)
                    : '',
                periodoInicial: value.periodoInicial
                    ? formatter.formatDateToPayload(value.periodoInicial)
                    : ''
            }
        }
        return payload
    }

    const setDataTotalizers = (response: {
        data: ManagementReportInterface
    }) => {
        const {
            sinteticoPorStatus,
            sinteticoPorImagem,
            sinteticoPorDetran,
            sinteticoPorTipoServico,
            sinteticoPorOrigem,
            estatistica
        } = response.data

        setListTotalizers({
            sinteticoPorStatus,
            sinteticoPorImagem,
            sinteticoPorDetran,
            sinteticoPorTipoServico,
            sinteticoPorOrigem,
            estatistica
        })
    }

    const fetchContractsWithCache = () => {
        const cacheFilters = getCacheFilters()!

        if (!cacheFilters) {
            fetchListReports({})
            return
        }

        const { page, limit, filter } = cacheFilters

        setPageReports(page)
        setLimitReports(limit)
        setFilter(filter)

        fetchListReports({
            query: { ...(filter ?? {}) },
            page: page ?? 1,
            limit: limit ?? 10
        })
    }

    const getDataReports = () => {
        if (!hasCacheFilters) {
            fetchListReports({})
            return
        }

        fetchContractsWithCache()
    }

    const fetchListReports = async ({
        query,
        page,
        limit
    }: RequestReportsDTO) => {
        setLoading(true)
        try {
            const filterParams = formatPayloadFilter(query)
            onFilter(filterParams)
            const response = await getManagementReports(
                {
                    pagina: page ?? pageReports,
                    limite: limit ?? limitReports,
                    query: buildQuery(filterParams)
                },
                currentInstitution?.id,
                isBackoffice
            )

            setTotalPages(+response.headers['total-pages'])
            setDataTotalizers(response)

            setTable((old: any) => ({
                ...old,
                body: buildTable(response.data)
            }))
        } finally {
            setLoading(false)
        }
    }

    const handleFilter = async (value: FilterManagementReport) => {
        setLoading(true)
        setPageReports(1)
        setLimitReports(limitReports)

        if (value) {
            const payloadFilter = formatPayloadFilter(value)
            setFilter(payloadFilter)
            onFilter(payloadFilter)
            fetchListReports({
                query: payloadFilter,
                page: 1,
                limit: limitReports
            })
        }
    }

    useEffect(() => {
        if (
            currentInstitution?.id !== undefined ||
            currentAgent?.razaoSocial !== undefined
        ) {
            getDataReports()
        }
    }, [currentInstitution?.id, currentAgent?.razaoSocial])

    return (
        <>
            <Filter handleFilter={handleFilter} />

            {listTotalizers && <Totalizers {...listTotalizers} />}

            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                loading={{
                    status: loading,
                    loader: defaultLoaders
                }}
                onAction={handleAction}
                pagination={{
                    page: pageReports,
                    limit: limitReports,
                    totalPages
                }}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            ></TableBuild>
        </>
    )
}
